import React, { useState, useEffect, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import supabase from '../config/supabase';
import { propagateGlobalCountFieldsToParents, formatPropagationToast } from '../services/sessionFieldPropagation';
import { buildParNormalizationPatch, isMissingParNormalizationColumnError } from '../utils/countUnitModel';

const Row = ({
  item,
  index,
  location,
  onMove,
  onMoveAcross,
  onRemove,
  onUpdatePar,
  onCommitPar
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'cs-item',
    item: { index, location, id: item.id },
    collect: m => ({ isDragging: m.isDragging() })
  });
  const [, drop] = useDrop({
    accept: 'cs-item',
    drop: d => {
      if (d.location !== location && onMoveAcross) {
        onMoveAcross(d.location, d.index, location, index);
        d.location = location;
        d.index = index;
      }
    },
    hover: d => {
      if (d.location !== location) return;
      if (d.index !== index) { onMove(location, d.index, index); d.index = index; }
    }
  });

  const snap = item.item_snapshot || {};
  const unit = item.par_unit || snap.unit || 'each';
  const desc = snap.description || item.description || 'Unknown Item';
  const brand = snap.brand || item.brand;
  const vendor = snap.vendor || item.vendor;
  const category = snap.category || item.category;
  const loc = item.location || snap.location || location || 'Unassigned';

  const handleItemClick = () => {
    // Trigger item selection for the middle column editor
    if (window.updateItemEditor) {
      window.updateItemEditor(item);
    }
  };

  return (
    <div
      ref={n => drag(drop(n))}
      className={`position-relative p-3 mb-2 bg-white border rounded ${isDragging ? 'opacity-50' : ''}`}
      style={{ minHeight: 64, cursor: 'move' }}
      onClick={handleItemClick}
    >
      {/* Position Number - Left Side */}
      <div className="position-absolute" style={{ top: '50%', left: '8px', transform: 'translateY(-50%)', zIndex: 1 }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: 'bold',
          color: '#000000',
          opacity: '0.5',
          lineHeight: '1'
        }}>
          {(item.count_position || index + 1)}
        </span>
      </div>

      {/* Drag Handle - Top Left Corner */}
      <div className="position-absolute text-muted" style={{ top: '8px', left: '8px', zIndex: 10, pointerEvents: 'none' }}>
        <i className="feather icon-menu" style={{ color: 'rgb(36, 38, 41)', fontWeight: '100', fontSize: '1rem' }}></i>
      </div>

      <div className="d-flex align-items-start justify-content-between" style={{ paddingLeft: '35px' }}>
        {/* Left Side - Item Info - Three Row Layout */}
        <div className="flex-grow-1" style={{ paddingRight: '90px', paddingTop: '1px' }}>
          {/* First Row - Item Name (Full Width) */}
          <div className="fw-bold mb-1" style={{
            fontSize: '0.8rem',
            color: 'var(--bs-emphasis-color)',
            fontWeight: '600',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            hyphens: 'auto',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            fontFamily: 'var(--bs-body-font-family)'
          }}>
            {desc}
          </div>

          {/* Second Row - Location with tab spacing */}
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--bs-secondary-color)',
            paddingLeft: '1ch', // One character space for tab alignment
            marginBottom: '2px'
          }}>
            {loc && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong style={{
                  color: 'var(--bs-emphasis-color)',
                  fontSize: '0.75rem',
                  marginRight: '6px',
                  flexShrink: 0,
                  minWidth: '25px'
                }}>
                  Loc:
                </strong>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px'
                }} title={loc}>
                  {loc}
                </span>
              </div>
            )}
          </div>

          {/* Third Row - Vendor aligned with Location */}
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--bs-secondary-color)',
            paddingLeft: '1ch' // Same tab alignment as location
          }}>
            {vendor && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong style={{
                  color: 'var(--bs-emphasis-color)',
                  fontSize: '0.75rem',
                  marginRight: '6px',
                  flexShrink: 0,
                  minWidth: '25px' // Same width as "Loc:" for alignment
                }}>
                  V:
                </strong>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px'
                }} title={vendor}>
                  {vendor}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - PAR Input - Fixed Position with Border */}
        <div
          style={{
            position: 'absolute',
            left: '299px', // 299px from left as shown in your CSS
            right: '12px', // 12px from right as shown in your CSS
            top: '0',
            bottom: '0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '4px',
            width: '75px',
            borderLeft: '2px solid var(--bs-border-color)',
            paddingLeft: '6px',
            paddingRight: '2px',
            backgroundColor: 'rgba(248, 249, 250, 0.5)',
            cursor: 'move'
          }}
        >
          {/* PAR Input */}
          <div className="d-flex align-items-center">
            <span className="me-1 fw-bold" style={{
              fontSize: '0.7rem',
              color: '#f0ad4e',
              minWidth: '25px'
            }}>
              PAR
            </span>
            <div className="position-relative">
              <input
                type="number"
                className="form-control"
                step="0.25"
                min="0"
                value={item.par_level ?? ''}
                onChange={e => onUpdatePar(item.id, loc, e.target.value, unit)}
                onBlur={() => onCommitPar(item.id, unit)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '38px', // Smaller to fit inside container
                  fontSize: '0.7rem',
                  padding: '2px 3px',
                  paddingRight: '12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #f0ad4e',
                  textAlign: 'left',
                  height: '24px'
                }}
                placeholder="0"
              />
              <span
                className="position-absolute text-muted"
                style={{
                  right: '3px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.6rem',
                  pointerEvents: 'none',
                  color: '#6c757d'
                }}
              >
                {(unit || 'each').substring(0, 2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Red X Delete Button */}
      <span
        onClick={() => onRemove(item.id, loc)}
        title="Remove"
        style={{
          position: 'absolute',
          left: '262px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#dc3545',
          cursor: 'pointer',
          zIndex: 1000,
          userSelect: 'none'
        }}
      >
        ×
      </span>
    </div>
  );
};

export default function CountSessionItemEditor({ sessionId, templateItems = {} }) {
  const [byLocation, setByLocation] = useState({});
  const [loading, setLoading] = useState(false);
  // Brief, non-blocking confirmation shown when an in-count PAR edit
  // successfully propagates to the parent template / inventory master.
  // See persistParValue() and Task #289.
  const [templateSyncToast, setTemplateSyncToast] = useState(null);
  const templateSyncToastTimerRef = useRef(null);
  const pendingParSaveTimersRef = useRef(new Map());
  const pendingParSaveValuesRef = useRef(new Map());
  const saveQueuesRef = useRef(new Map());

  const showTemplateSyncToast = (msg) => {
    if (!msg) return;
    setTemplateSyncToast(msg);
    clearTimeout(templateSyncToastTimerRef.current);
    templateSyncToastTimerRef.current = setTimeout(() => setTemplateSyncToast(null), 3000);
  };
  // Mirror byLocation so save callbacks running after debounce timers can
  // resolve a count_items.id back to its inventory_item_id without relying
  // on the (possibly stale) closure value of byLocation.
  const byLocationRef = useRef({});

  useEffect(() => { sessionId ? loadCountItems() : loadFromTemplates(); }, [sessionId, templateItems]);

  useEffect(() => { byLocationRef.current = byLocation; }, [byLocation]);

  // Resolve a count_items.id to its inventory_item_id from the current
  // grouped state. Returns null if the row hasn't been loaded yet.
  // Only returns the true inventory FK — never falls back to a snapshot id
  // because snapshot ids don't match count_template_items.inventory_item_id.
  const findInventoryItemIdForRow = (countItemId) => {
    const buckets = byLocationRef.current || {};
    for (const items of Object.values(buckets)) {
      const found = (items || []).find((it) => String(it.id) === String(countItemId));
      if (found) return found.inventory_item_id || null;
    }
    return null;
  };

  useEffect(() => {
    return () => {
      pendingParSaveTimersRef.current.forEach((timer) => clearTimeout(timer));
      pendingParSaveTimersRef.current.clear();
      pendingParSaveValuesRef.current.clear();
      saveQueuesRef.current.clear();
      clearTimeout(templateSyncToastTimerRef.current);
    };
  }, []);

  const groupByLocation = items => {
    const sorted = [...(items || [])].sort((a, b) => {
      const locationPosA = Number.parseFloat(a.location_position);
      const locationPosB = Number.parseFloat(b.location_position);
      const hasLocationPosA = Number.isFinite(locationPosA) && locationPosA > 0;
      const hasLocationPosB = Number.isFinite(locationPosB) && locationPosB > 0;
      if (hasLocationPosA && hasLocationPosB && locationPosA !== locationPosB) return locationPosA - locationPosB;
      if (hasLocationPosA && !hasLocationPosB) return -1;
      if (!hasLocationPosA && hasLocationPosB) return 1;

      const locationA = (a.location || a.item_snapshot?.location || '').toLowerCase();
      const locationB = (b.location || b.item_snapshot?.location || '').toLowerCase();
      if (locationA !== locationB) return locationA.localeCompare(locationB);

      const countPosA = Number.parseFloat(a.count_position);
      const countPosB = Number.parseFloat(b.count_position);
      const hasCountPosA = Number.isFinite(countPosA) && countPosA > 0;
      const hasCountPosB = Number.isFinite(countPosB) && countPosB > 0;
      if (hasCountPosA && hasCountPosB && countPosA !== countPosB) return countPosA - countPosB;
      if (hasCountPosA && !hasCountPosB) return -1;
      if (!hasCountPosA && hasCountPosB) return 1;

      const descA = (a.item_snapshot?.description || a.description || a.name || '').toLowerCase();
      const descB = (b.item_snapshot?.description || b.description || b.name || '').toLowerCase();
      return descA.localeCompare(descB);
    });

    const g = {};
    sorted.forEach(it => {
      const loc = it.location || it.item_snapshot?.location || 'Unassigned';
      (g[loc] ||= []).push(it);
    });
    Object.keys(g).forEach(loc => g[loc].sort((a,b)=> (a.count_position||0)-(b.count_position||0)));
    return g;
  };

  const loadCountItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('count_items')
      .select('*')
      .eq('count_session_id', sessionId)
      .order('location_position', { ascending: true, nullsFirst: false })
      .order('count_position', { ascending: true, nullsFirst: false });
    if (!error) {
      setByLocation(groupByLocation(data || []));
    }
    setLoading(false);
  };

  const loadFromTemplates = () => {
    const flat = Object.values(templateItems || {}).flat().map((it, i) => ({
      ...it, id: it.id, count_position: it.count_position || i+1, par_level: it.par_level
    }));
    setByLocation(groupByLocation(flat));
  };

  const parseNullableQuantity = (value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const queueCountItemSave = (id, updateBuilder) => {
    const previous = saveQueuesRef.current.get(id) || Promise.resolve();
    const next = previous
      .catch(() => {})
      .then(updateBuilder)
      .catch((error) => {
        console.error('Error saving count item edit:', error);
      });
    saveQueuesRef.current.set(id, next);
    return next;
  };

  // The row itself, for the normalization patch's count unit.
  const findRowById = (countItemId) => {
    const buckets = byLocationRef.current || {};
    for (const items of Object.values(buckets)) {
      const found = (items || []).find((it) => String(it.id) === String(countItemId));
      if (found) return found;
    }
    return null;
  };

  const persistParValue = (id, unit = 'each') => {
    if (!sessionId) return Promise.resolve();
    const parsedPar = parseNullableQuantity(pendingParSaveValuesRef.current.get(id));
    return queueCountItemSave(id, async () => {
      // The legacy pair moves WITH the stored normalized PAR (shared builder,
      // countUnitModel) — otherwise a stale par_normalized_quantity keeps
      // winning in every resolved reader while this edit shows on screen.
      // Degrades to the pair alone on undeployed columns.
      const row = findRowById(id);
      const legacyPatch = {
        par_level: parsedPar,
        par_unit: unit || 'each',
        par_defaulted: false,
      };
      let result = await supabase
        .from('count_items')
        .update({
          ...legacyPatch,
          ...buildParNormalizationPatch({
            parLevel: parsedPar,
            parUnit: unit,
            countUnit: row?.item_snapshot?.unit ?? row?.count_unit ?? null,
          }),
        })
        .eq('id', id);
      if (result.error && isMissingParNormalizationColumnError(result.error)) {
        result = await supabase
          .from('count_items')
          .update(legacyPatch)
          .eq('id', id);
      }
      // Propagate up to the parent template (count_template_items.par_level)
      // so the next count from this template pre-fills the user's value.
      // Awaited so we can show a brief confirmation toast on success
      // (Task #289).
      const invId = findInventoryItemIdForRow(id);
      if (invId) {
        try {
          const propagation = await propagateGlobalCountFieldsToParents({
            sessionId,
            inventoryItemId: invId,
            par_level: parsedPar,
            par_defaulted: false,
            par_unit: unit || 'each',
          });
          const msg = formatPropagationToast(propagation);
          if (msg) showTemplateSyncToast(msg);
        } catch (e) {
          console.warn('[CountSessionItemEditor] PAR propagation failed:', e?.message);
        }
      }
      return result;
    });
  };

  const scheduleParPersistence = (id, unit = 'each') => {
    const existing = pendingParSaveTimersRef.current.get(id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      pendingParSaveTimersRef.current.delete(id);
      void persistParValue(id, unit);
    }, 350);
    pendingParSaveTimersRef.current.set(id, timer);
  };

  const flushParPersistence = async (id, unit = 'each') => {
    const timer = pendingParSaveTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      pendingParSaveTimersRef.current.delete(id);
    }
    await persistParValue(id, unit);
  };

  const updatePar = (id, location, par, unit) => {
    pendingParSaveValuesRef.current.set(id, par);
    setByLocation(prev => ({
      ...prev,
      [location]: prev[location].map(i => i.id === id
        ? { ...i, par_level: par, par_unit: unit, par_defaulted: false }
        : i),
    }));
    scheduleParPersistence(id, unit);
  };

  const commitParOnBlur = async (id, unit = 'each') => {
    await flushParPersistence(id, unit);
  };

  const moveWithin = async (location, from, to) => {
    const list = [...byLocation[location]];
    const [m] = list.splice(from,1); list.splice(to,0,m);
    const updated = list.map((it, idx) => ({ ...it, count_position: idx+1 }));
    setByLocation(prev => ({ ...prev, [location]: updated }));
    if (sessionId) {
      for (const it of updated) {
        await supabase.from('count_items')
          .update({ count_position: it.count_position })
          .eq('id', it.id)
          .eq('count_session_id', sessionId);
      }
    }
  };

  const moveAcrossLocations = async (sourceLocation, sourceIndex, destLocation, destIndex) => {
    const sourceList = [...(byLocation[sourceLocation] || [])];
    const destList = sourceLocation === destLocation ? sourceList : [...(byLocation[destLocation] || [])];
    const [movedItem] = sourceList.splice(sourceIndex, 1);
    if (!movedItem) return;
    const updatedItem = { ...movedItem, location: destLocation };
    destList.splice(destIndex, 0, updatedItem);

    const reindexedSource = sourceList.map((it, idx) => ({ ...it, count_position: idx + 1 }));
    const reindexedDest = destList.map((it, idx) => ({ ...it, count_position: idx + 1 }));

    setByLocation(prev => {
      const next = { ...prev };
      next[sourceLocation] = reindexedSource;
      next[destLocation] = reindexedDest;
      if (reindexedSource.length === 0) delete next[sourceLocation];
      return next;
    });

    if (sessionId) {
      await supabase.from('count_items')
        .update({ location: destLocation })
        .eq('id', movedItem.id)
        .eq('count_session_id', sessionId);
      for (const it of reindexedSource) {
        await supabase.from('count_items')
          .update({ count_position: it.count_position })
          .eq('id', it.id)
          .eq('count_session_id', sessionId);
      }
      for (const it of reindexedDest) {
        await supabase.from('count_items')
          .update({ count_position: it.count_position })
          .eq('id', it.id)
          .eq('count_session_id', sessionId);
      }
    }
  };

  const removeItem = async (id, location) => {
    if (sessionId) await supabase.from('count_items').delete().eq('id', id).eq('count_session_id', sessionId);
    setByLocation(prev => {
      const next = { ...prev };
      const remaining = (next[location] || []).filter(i => i.id !== id);
      next[location] = remaining.map((it, idx) => ({ ...it, count_position: idx + 1 }));
      if (next[location].length === 0) delete next[location];
      return next;
    });
  };

  if (loading) return <div className="text-muted">Loading...</div>;
  const locations = Object.keys(byLocation);
  if (!locations.length) return <div className="text-muted">Select templates to begin editing PAR and order.</div>;

  return (
    <DndProvider backend={HTML5Backend}>
      {locations.map(loc => (
        <div key={loc} className="mb-4">
          <h6 className="fw-bold text-primary mb-2"><i className="feather icon-map-pin me-2"></i>{loc} ({byLocation[loc].length})</h6>
          {byLocation[loc].map((it, idx) => (
            <Row
              key={it.id}
              item={it}
              index={idx}
              location={loc}
              onMove={moveWithin}
              onMoveAcross={moveAcrossLocations}
              onRemove={removeItem}
              onUpdatePar={updatePar}
              onCommitPar={commitParOnBlur}
            />
          ))}
        </div>
      ))}

      {/* ── Template-sync confirmation toast (Task #289) ─────────────────────
          Brief, non-blocking confirmation that an in-count PAR edit
          successfully propagated to the parent template (or, in fallback
          cases, to the inventory master). */}
      {templateSyncToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#2f6f3a',
            color: '#fff',
            padding: '8px 18px',
            borderRadius: '20px',
            fontSize: '0.82rem',
            fontWeight: 600,
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: '85vw',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
          }}
        >
          {templateSyncToast}
        </div>
      )}
    </DndProvider>
  );
}
